/**
 * Utility functions for processing tool response content
 */

import type { ToolCallLog } from "../types/chat";

/**
 * Attempts to parse JSON from content string
 * @param content - The content string that may contain JSON
 * @returns Parsed JSON object or null if parsing fails
 */
const tryParseJSON = (content: string): unknown | null => {
  try {
    if (!content.includes("{") || !content.includes("}")) {
      return null;
    }

    const jsonMatch = content.match(/(\{.*\})/s);
    if (!jsonMatch) {
      return null;
    }

    return JSON.parse(jsonMatch[1]);
  } catch {
    return null;
  }
};

/**
 * Extracts fund IDs from an array response
 * @param data - Array of fund information objects
 * @returns Array of fund IDs/product codes
 */
const extractFundIds = (data: unknown[]): string[] => {
  return data
    .map((fundInfo: unknown) => {
      if (typeof fundInfo === "object" && fundInfo !== null) {
        const info = fundInfo as Record<string, unknown>;
        return info.fund_id || info.product_code;
      }
      return null;
    })
    .filter(Boolean) as string[];
};

/**
 * Handles array response formatting
 * @param data - Array response data
 * @returns User-friendly message or null if not handled
 */
const handleArrayResponse = (data: unknown[]): string | null => {
  if (data.length === 0) {
    return "Got an answer";
  }

  const fundIds = extractFundIds(data);
  if (fundIds.length > 0) {
    return `Got the funds ${fundIds.join(", ")} details`;
  }

  return "Got an answer";
};

/**
 * Handles object response formatting by checking common response patterns
 * @param response - Object response data
 * @returns User-friendly message or null if not handled
 */
const handleObjectResponse = (
  response: Record<string, unknown>
): string | null => {
  // ask needl generative tool call
  if (
    response.generated_answer &&
    typeof response.generated_answer === "object" &&
    response.generated_answer !== null
  ) {
    const generatedAnswer = response.generated_answer as Record<
      string,
      unknown
    >;
    if (generatedAnswer.answer) {
      return String(generatedAnswer.answer);
    }
  }

  // ask needl retrieval tool call
  if (response.retrieved_results && Array.isArray(response.retrieved_results)) {
    return `Found ${response.retrieved_results.length} results from sources.`;
  }

  // TODO: cleanup all below ones later
  if (response.answer) {
    return String(response.answer);
  }

  if (response.result) {
    return String(response.result);
  }

  // Fund-specific fields
  if (
    response.matching_funds &&
    Array.isArray(response.matching_funds) &&
    response.matching_funds.length > 0
  ) {
    return `Found these funds: ${response.matching_funds.join(", ")}`;
  }

  if (response.fund_id) {
    return `Got the fund ${response.fund_id} details`;
  }

  // General descriptive fields
  if (response.message) {
    return String(response.message);
  }

  if (response.summary) {
    return String(response.summary);
  }

  if (response.description) {
    return String(response.description);
  }

  // Fallback for any object with data
  if (Object.keys(response).length > 0) {
    return "Got an answer";
  }

  return null;
};

/**
 * Extracts user-friendly content from a tool response JSON
 * @param content - The JSON string content from the tool response
 * @returns User-friendly extracted content or fallback message
 */
const extractUserFriendlyContent = (content: string): string => {
  const parsed = tryParseJSON(content);

  if (!parsed) {
    return "Got an answer";
  }

  try {
    // Handle array responses
    if (Array.isArray(parsed)) {
      return handleArrayResponse(parsed) || "Got an answer";
    }

    // Handle object responses
    if (typeof parsed === "object" && parsed !== null) {
      const response = parsed as Record<string, unknown>;
      return handleObjectResponse(response) || "Got an answer";
    }

    return "Got an answer";
  } catch (error) {
    console.warn("Failed to extract user-friendly content:", error);
    return "Got an answer";
  }
};

/**
 * Extracts query information from tool arguments
 * @param content - Raw content string containing arguments
 * @returns Formatted query string or fallback message
 */
const extractQueryFromArguments = (content: string): string => {
  const parsed = tryParseJSON(content);

  if (!parsed || typeof parsed !== "object" || parsed === null) {
    return "Processing your request...";
  }

  const args = parsed as Record<string, unknown>;

  // Check for different query field names
  if (args.query_string) {
    return `Searching for: ${args.query_string}`;
  }
  if (args.query) {
    return `Searching for: ${args.query}`;
  }
  if (args.prompt) {
    return `Processing: ${args.prompt}`;
  }

  return "Processing your request...";
};

/**
 * Extracts tool name from TOOL_CALL_INITIATED content
 * @param content - The content string from the initiated event
 * @returns Extracted tool name or fallback
 */
export const extractToolName = (content: string): string => {
  if (!content || typeof content !== "string") {
    return "unnamed_tool";
  }

  if (content.includes("Tool: ")) {
    const toolNameMatch = content.match(/Tool: ([a-zA-Z0-9_]+)/);
    return toolNameMatch ? toolNameMatch[1] : "unnamed_tool";
  }

  return "unnamed_tool";
};

/**
 * Processes reasoning content for display
 * @param summary - Clean summary without prefix (structured field)
 * @param isDevMode - Whether the display is in dev mode
 * @param contentFallback - Old content field for backward compatibility
 * @param shortSummary - Array of bullet points for user mode
 * @returns Formatted reasoning content
 */
export const formatReasoningContent = (
  summary: string | undefined,
  isDevMode: boolean,
  contentFallback?: string,
  shortSummary?: string[]
): string => {
  // In dev mode, show full summary as before
  if (isDevMode) {
    const content = summary || contentFallback;
    if (!content) {
      return "";
    }

    // Remove old prefix if present (for backward compatibility)
    let cleanContent = content;
    if (cleanContent.startsWith("[REASONING SUMMARY] - ")) {
      cleanContent = cleanContent.substring("[REASONING SUMMARY] - ".length);
    }

    return cleanContent;
  }

  // In user mode, prefer short_summary bullet points
  if (shortSummary && shortSummary.length > 0) {
    return shortSummary.map((item) => `• ${item}`).join("\n");
  }

  // Fallback to regular summary or content
  const content = summary || contentFallback;
  if (!content) {
    return "";
  }

  // Remove old prefix if present (for backward compatibility)
  let cleanContent = content;
  if (cleanContent.startsWith("[REASONING SUMMARY] - ")) {
    cleanContent = cleanContent.substring("[REASONING SUMMARY] - ".length);
  }

  return cleanContent;
};

/**
 * Formats tool initiated content for display
 * @param displayName - User-friendly display name (structured field)
 * @param toolName - Technical tool name (for dev mode)
 * @param isDevMode - Whether the display is in dev mode
 * @param contentFallback - Old content field for backward compatibility
 * @returns Formatted content
 */
export const formatToolInitiatedContent = (
  displayName: string | undefined,
  toolName: string | undefined,
  isDevMode: boolean,
  contentFallback?: string
): string => {
  if (isDevMode) {
    // In dev mode, show technical tool name
    if (toolName) return toolName;
    // Fallback: extract from old content field
    if (contentFallback) {
      const extractedName = extractToolName(contentFallback);
      return extractedName !== "unnamed_tool" ? extractedName : "Tool started";
    }
    return "Tool started";
  }

  // In user mode, show display name
  if (displayName) return displayName;

  // Fallback for old format: extract tool name and format it nicely
  if (contentFallback) {
    const extractedName = extractToolName(contentFallback);
    if (extractedName !== "unnamed_tool") {
      return formatToolNameForDisplay(extractedName);
    }
  }

  return "Getting information...";
};

/**
 * Formats tool arguments content for display
 * @param structuredArguments - Structured arguments object
 * @param isDevMode - Whether the display is in dev mode
 * @param contentFallback - Old content field for backward compatibility
 * @returns Formatted content
 */
export const formatToolArgumentsContent = (
  structuredArguments: unknown | undefined,
  isDevMode: boolean,
  contentFallback?: string
): string => {
  // Try new structured field first, fall back to old content
  const argsToFormat =
    structuredArguments ||
    (contentFallback ? tryParseJSON(contentFallback) : null);

  if (!argsToFormat) {
    // If no data available, use old content as-is or show default
    if (contentFallback) {
      return isDevMode
        ? contentFallback
        : extractQueryFromArguments(contentFallback);
    }
    return isDevMode ? "No arguments" : "Processing your request...";
  }

  if (isDevMode) {
    try {
      return JSON.stringify(argsToFormat, null, 2);
    } catch {
      return contentFallback || "Invalid arguments";
    }
  }

  // For user mode, show a formatted version of key arguments
  try {
    if (typeof argsToFormat === "object" && argsToFormat !== null) {
      const args = argsToFormat as Record<string, unknown>;

      // Check for common query fields
      if (args.query_string) {
        return `Query: "${args.query_string}"`;
      }
      if (args.query) {
        return `Query: "${args.query}"`;
      }
      if (args.question) {
        return `Question: "${args.question}"`;
      }
      if (args.prompt) {
        return `Prompt: "${args.prompt}"`;
      }

      // For other arguments, show key-value pairs nicely
      const entries = Object.entries(args).slice(0, 3); // Show max 3 fields
      if (entries.length > 0) {
        const formatted = entries
          .map(([key, value]) => {
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase());
            const valueStr =
              typeof value === "string" ? value : JSON.stringify(value);
            return `${formattedKey}: ${valueStr.length > 50 ? valueStr.substring(0, 50) + "..." : valueStr}`;
          })
          .join("\n");
        return formatted;
      }
    }

    // Fallback for non-object arguments
    const argsString =
      typeof argsToFormat === "string"
        ? argsToFormat
        : JSON.stringify(argsToFormat);
    return argsString.length > 100
      ? argsString.substring(0, 100) + "..."
      : argsString;
  } catch {
    // Final fallback to old content or default
    if (contentFallback) {
      return isDevMode
        ? contentFallback
        : extractQueryFromArguments(contentFallback);
    }
    return "Processing your request...";
  }
};

/**
 * Formats tool finished content for display
 * @param structuredOutput - Structured output object
 * @param error - Error message or object if tool failed
 * @param isDevMode - Whether the display is in dev mode
 * @param contentFallback - Old content field for backward compatibility
 * @returns Formatted content
 */
export const formatToolFinishedContent = (
  structuredOutput: unknown | undefined,
  error: string | { message: string; [key: string]: unknown } | undefined,
  isDevMode: boolean,
  contentFallback?: string
): string => {
  // Handle error case
  if (error) {
    const errorMessage = typeof error === "string" ? error : error.message;
    return isDevMode ? `Error: ${errorMessage}` : `Failed: ${errorMessage}`;
  }

  // Try new structured field first, fall back to old content
  const outputToFormat =
    structuredOutput ||
    (contentFallback ? tryParseJSON(contentFallback) : null);

  // Handle success case
  if (!outputToFormat) {
    // If no structured output, use old content as-is or show default
    if (contentFallback) {
      return isDevMode
        ? contentFallback
        : extractUserFriendlyContent(contentFallback);
    }
    return isDevMode ? "No output" : "Got an answer";
  }

  if (isDevMode) {
    try {
      return JSON.stringify(outputToFormat, null, 2);
    } catch {
      return contentFallback || "Invalid output";
    }
  }

  // For user mode, extract user-friendly content
  try {
    const outputString =
      typeof outputToFormat === "string"
        ? outputToFormat
        : JSON.stringify(outputToFormat);
    return extractUserFriendlyContent(outputString);
  } catch {
    // Final fallback to old content or default
    if (contentFallback) {
      return extractUserFriendlyContent(contentFallback);
    }
    return "Got an answer";
  }
};

/**
 * Accordion-specific utility functions
 */

/**
 * Formats tool name for display (removes underscores, capitalizes words)
 * @param name - Raw tool name
 * @returns Formatted tool name for user display
 */
export const formatToolNameForDisplay = (name: string): string => {
  if (!name) return "Tool";
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Gets the display mode from localStorage
 * @returns Whether dev mode is enabled
 */
export const getDisplayMode = (): boolean => {
  const savedMode = localStorage.getItem("displayMode");
  return savedMode === "dev";
};

/**
 * Creates a storage change event listener for display mode
 * @param setIsDevMode - State setter for dev mode
 * @returns Cleanup function for the event listener
 */
export const createDisplayModeListener = (
  setIsDevMode: (value: boolean) => void
): (() => void) => {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === "displayMode") {
      setIsDevMode(event.newValue === "dev");
    }
  };

  window.addEventListener("storage", handleStorageChange);
  return () => window.removeEventListener("storage", handleStorageChange);
};

/**
 * Checks if a tool is still in progress (no TOOL_FINISHED call yet)
 * @param toolCalls - Array of tool call logs
 * @returns Whether the tool is still in progress
 */
export const isToolInProgress = (toolCalls: { type: string }[]): boolean => {
  return !toolCalls.some((call) => call.type === "TOOL_FINISHED");
};

/**
 * Filters tool calls for user mode display (only shows important calls)
 * @param toolCalls - Array of tool call logs
 * @param isDevMode - Whether dev mode is enabled
 * @returns Filtered array of tool calls
 */
export const filterToolCallsForDisplay = <T extends { type: string }>(
  toolCalls: T[],
  isDevMode: boolean
): T[] => {
  if (isDevMode) {
    return toolCalls;
  }

  // In user mode, show TOOL_ARGUMENTS and TOOL_FINISHED
  return toolCalls.filter(
    (call) => call.type === "TOOL_ARGUMENTS" || call.type === "TOOL_FINISHED"
  );
};

/**
 * Gets the display label for tool call type in dev mode
 * @param callType - Type of tool call
 * @returns User-friendly label
 */
export const getToolCallTypeLabel = (callType: string): string => {
  switch (callType) {
    case "TOOL_INITIATED":
      return "Starting";
    case "TOOL_ARGUMENTS":
      return "Arguments";
    case "TOOL_FINISHED":
      return "Result";
    case "REASONING":
      return "Reasoning";
    default:
      return "Event";
  }
};

/**
 * Helper function to set display mode (for testing/debugging)
 * @param mode - "dev" or "user" mode
 */
export const setDisplayMode = (mode: "dev" | "user"): void => {
  localStorage.setItem("displayMode", mode);
  // Manually trigger storage event for same-tab testing
  window.dispatchEvent(
    new StorageEvent("storage", {
      key: "displayMode",
      newValue: mode,
      oldValue: localStorage.getItem("displayMode"),
      storageArea: localStorage,
    })
  );
};

// Expose setDisplayMode globally for testing in browser console
if (typeof window !== "undefined") {
  (
    window as unknown as { setDisplayMode: typeof setDisplayMode }
  ).setDisplayMode = setDisplayMode;
}

/**
 * Extracts question from tool arguments for user mode display
 * @param structuredArguments - Tool arguments object
 * @param contentFallback - Old content field for backward compatibility
 * @returns Clean question string
 */
export const extractQuestionFromArguments = (
  structuredArguments: unknown | undefined,
  contentFallback?: string
): string => {
  // Try structured arguments first
  const argsToCheck =
    structuredArguments ||
    (contentFallback ? tryParseJSON(contentFallback) : null);

  if (argsToCheck && typeof argsToCheck === "object" && argsToCheck !== null) {
    const args = argsToCheck as Record<string, unknown>;

    // Check for common query field names
    if (args.query_string && typeof args.query_string === "string") {
      return args.query_string;
    }
    if (args.query && typeof args.query === "string") {
      return args.query;
    }
    if (args.question && typeof args.question === "string") {
      return args.question;
    }
    if (args.prompt && typeof args.prompt === "string") {
      return args.prompt;
    }
  }

  return "Processing query...";
};

type ExtendedToolCallLog = ToolCallLog & { [key: string]: unknown };

export const groupUserToolCalls = (
  toolCallLogs: ExtendedToolCallLog[]
): {
  [key: string]: {
    logs: ExtendedToolCallLog[];
    questions: { text: string; completed: boolean; tool_id?: string }[];
    toolName: string;
  };
} => {
  // Build a tool_id → metadata map from INITIATED events so that
  // ARGUMENTS/FINISHED events (which may lack display_name/tool_name
  // in old session data) can resolve them
  const toolMeta: Record<
    string,
    { display_name?: string; tool_name?: string }
  > = {};
  toolCallLogs.forEach((log) => {
    if (log.type === "TOOL_INITIATED" && log.tool_id) {
      toolMeta[log.tool_id] = {
        display_name: log.display_name,
        tool_name: log.tool_name,
      };
    }
  });

  // Collect all tool_ids that have a TOOL_FINISHED event
  const finishedToolIds = new Set(
    toolCallLogs
      .filter((log) => log.type === "TOOL_FINISHED")
      .map((log) => log.tool_id)
      .filter(Boolean)
  );

  const toolGroups: {
    [key: string]: {
      logs: ExtendedToolCallLog[];
      questions: { text: string; completed: boolean; tool_id?: string }[];
      toolName: string;
    };
  } = {};

  const resolveToolName = (log: ExtendedToolCallLog): string => {
    const meta = log.tool_id ? toolMeta[log.tool_id] : undefined;
    const displayName = log.display_name || meta?.display_name;
    if (displayName) return displayName;
    const rawName = log.tool_name || meta?.tool_name;
    if (rawName) return formatToolNameForDisplay(rawName);
    return "Tool";
  };

  toolCallLogs.forEach((log) => {
    if (
      log.type === "TOOL_INITIATED" ||
      log.type === "TOOL_ARGUMENTS" ||
      log.type === "TOOL_FINISHED"
    ) {
      const toolName = resolveToolName(log);
      const groupKey = `grouped-${toolName.toLowerCase().replace(/\s+/g, "-")}`;

      if (!toolGroups[groupKey]) {
        toolGroups[groupKey] = {
          logs: [],
          questions: [],
          toolName,
        };
      }

      toolGroups[groupKey].logs.push(log);

      if (log.type === "TOOL_ARGUMENTS") {
        const question = extractQuestionFromArguments(
          log.arguments,
          log.content
        );
        toolGroups[groupKey].questions.push({
          text: question,
          completed: log.tool_id ? finishedToolIds.has(log.tool_id) : false,
          tool_id: log.tool_id,
        });
      }
    }
  });

  return toolGroups;
};

/**
 * Determines the current active section title for dynamic header
 * @param toolCallLogs - Array of tool call logs
 * @param isStreaming - Whether currently streaming
 * @returns Dynamic header title
 */
export const getCurrentActiveSection = (
  toolCallLogs: ToolCallLog[],
  isStreaming: boolean
): string => {
  if (!isStreaming || toolCallLogs.length === 0) {
    return "Processing...";
  }

  const resolveDisplayName = (log: ToolCallLog): string => {
    if (log.display_name) return log.display_name;
    if (log.tool_id) {
      const initiated = toolCallLogs.find(
        (l) => l.type === "TOOL_INITIATED" && l.tool_id === log.tool_id
      );
      if (initiated?.display_name) return initiated.display_name;
      if (initiated?.tool_name)
        return formatToolNameForDisplay(initiated.tool_name);
    }
    return formatToolNameForDisplay(log.tool_name || "Tool");
  };

  const sortedLogs = [...toolCallLogs].sort(
    (a, b) => b.timestamp - a.timestamp
  );
  const mostRecent = sortedLogs[0];

  if (mostRecent.type === "REASONING") {
    return "Reasoning & thinking";
  }

  if (
    mostRecent.type === "TOOL_INITIATED" ||
    mostRecent.type === "TOOL_ARGUMENTS"
  ) {
    return resolveDisplayName(mostRecent);
  }

  // Look for any in-progress tool calls
  const inProgressTools = toolCallLogs.filter(
    (log) =>
      (log.type === "TOOL_INITIATED" || log.type === "TOOL_ARGUMENTS") &&
      !toolCallLogs.some(
        (finishedLog) =>
          finishedLog.type === "TOOL_FINISHED" &&
          log.tool_id &&
          finishedLog.tool_id === log.tool_id
      )
  );

  if (inProgressTools.length > 0) {
    const latest = inProgressTools.reduce((best, log) =>
      log.timestamp > best.timestamp ? log : best
    );
    return resolveDisplayName(latest);
  }

  return "Processing...";
};
